import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import AccordionCard from 'components/Shared/AccordionCard'
import LoadingSpinner, { SpinnerSize } from 'components/Shared/LoadingSpinner'

import { GeoTargetingField } from './GeoTargetingField'
import { useTargeting, useTargetingDispatch } from './TargetingContext'
import {
  TARGETING_MODES,
  TARGETING_PLATFORMS,
  activeTargetingPlatforms,
  gatedTargetingPlatforms,
  isGoogleDisplaySetup,
  isLocationTargetingEmpty,
  validateLocationTargeting,
  withCampaignWideTargetingFallback,
  actions as targetingActions,
} from './targeting'

import greenCheck from 'images/icons/check-green1.svg'
import {
  useCampaignSetupDispatch,
  useCampaignSetup,
} from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { derivePlatformSelectionFromSetup } from 'components/Recommendations/Campaign/platform'
import { hasAudienceTargeting } from 'components/Recommendations/Campaign/Context/util'
import { SPECIAL_AD_CATEGORY } from 'api/models'
import { MetaAudienceTargetingField } from './MetaAudienceTargetingField'
import { GoogleAudienceTargetingField } from './GoogleAudienceTargetingField'

export default function Targeting({
  event,
  tc,
  tc_run_id,
  disabled,
  isPublished = false,
  onChange,
  showValidationErrors = false,
  savedAudienceSelected = false,
  isFirstTc = false,
}) {
  const { t } = useTranslation()

  const { value: targeting, error, success, loading } = useTargeting()
  const dispatchTargetingAction = useTargetingDispatch()
  const { setup } = useCampaignSetup()
  const { overwriteTCTargeting } = useCampaignSetupDispatch()
  const platforms = useMemo(
    () => activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup)),
    [setup],
  )

  const tcTargeting = useMemo(() => targeting.find((x) => x.tc === tc) ?? { tc }, [targeting, tc])
  const effectiveTcTargeting = useMemo(
    () => withCampaignWideTargetingFallback(tcTargeting, setup, isFirstTc),
    [tcTargeting, setup, isFirstTc],
  )
  const hasCustomAudience = useMemo(
    () => hasAudienceTargeting(effectiveTcTargeting),
    [effectiveTcTargeting],
  )

  // Once campaign_targeting is the source (Google Display, or Meta+Google
  // Display combined), only TC #1's GEO field (GeoTargetingField below) is
  // editable — every other TC's geo renders read-only, sourced from
  // campaign_targeting via GeoTargetingField's own fallback. The audience
  // fields are not gated by that at all: they are editable on every TC, but
  // only until publish (audienceDisabled below).
  // isFirstTc is caller-supplied (see targeting.js) — never derived from
  // setup.creatives here.
  const gatedPlatforms = useMemo(() => gatedTargetingPlatforms(setup), [setup])
  const isCampaignWideGated = gatedPlatforms.length > 0 && !isFirstTc
  const targetingDisabled = disabled || isCampaignWideGated
  const audienceDisabled = disabled || isPublished

  const hasTargetingLimitations =
    event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.employment) ||
    event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.financial) ||
    event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.housing) ||
    event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.political)

  const onChangeInternal = useCallback(
    (tcTargeting) => {
      const { targetingMode, skipNetworkSync = false, ...platformPayload } = tcTargeting
      if (!targetingMode) {
        return
      }

      const tcPayload = {
        ...targeting.find((x) => x.tc === tc),
        tc,
        tc_run_id,
        ...platformPayload,
      }

      dispatchTargetingAction({ type: targetingActions.setLoading })
      overwriteTCTargeting(
        tcPayload,
        (success) => {
          if (success) {
            dispatchTargetingAction({ type: targetingActions.setSuccess })
          } else {
            dispatchTargetingAction({
              type: targetingActions.setError,
              payload: 'common.errors.inServerResponse',
            })
          }
        },
        isFirstTc,
        skipNetworkSync,
      )
      onChange([
        {
          tc,
          ...tcPayload,
        },
      ])
    },
    [overwriteTCTargeting, dispatchTargetingAction, tc, tc_run_id, targeting, onChange, isFirstTc],
  )

  // Audience selections have no targeting mode, unlike onChangeInternal's
  // GeoTargetingField-only contract (which no-ops without one) — same
  // dispatch+sync, minus that guard.
  const onAudienceChangeInternal = useCallback(
    (platformPayload) => {
      const tcPayload = {
        ...targeting.find((x) => x.tc === tc),
        tc,
        tc_run_id,
        ...platformPayload,
      }

      dispatchTargetingAction({ type: targetingActions.setLoading })
      overwriteTCTargeting(
        tcPayload,
        (success) => {
          if (success) {
            dispatchTargetingAction({ type: targetingActions.setSuccess })
          } else {
            dispatchTargetingAction({
              type: targetingActions.setError,
              payload: 'common.errors.inServerResponse',
            })
          }
        },
        isFirstTc,
      )
      onChange([{ tc, ...tcPayload }])
    },
    [overwriteTCTargeting, dispatchTargetingAction, tc, tc_run_id, targeting, onChange, isFirstTc],
  )

  const showSuccess = useMemo(() => {
    const tcTargeting = targeting.find((x) => x.tc === tc)
    if (!tcTargeting) {
      return false
    }

    const currentTargetingMode = platforms.map((p) => tcTargeting[p]?.targeting_mode).find(Boolean)

    return (
      !loading &&
      success &&
      // An untouched TC (no fb/google sub-object set at all yet) must not
      // show success just because some *other* TC's save flipped the shared
      // `success` flag — validateLocationTargeting/isLocationTargetingEmpty
      // both vacuously pass an empty tcTargeting (same as the old v3 code
      // did for a TC with no targetingMode set), so this needs its own guard.
      !!currentTargetingMode &&
      validateLocationTargeting(
        targeting,
        tc,
        tc_run_id,
        hasCustomAudience,
        savedAudienceSelected,
        platforms,
        setup,
      ).valid &&
      !platforms.some((p) => isLocationTargetingEmpty(tcTargeting, p)) &&
      currentTargetingMode !== TARGETING_MODES.fdTargeting
    )
  }, [
    loading,
    success,
    targeting,
    tc,
    tc_run_id,
    hasCustomAudience,
    savedAudienceSelected,
    platforms,
    setup,
  ])

  const isGoogleDisplay = useMemo(() => isGoogleDisplaySetup(setup), [setup])

  return (
    <AccordionCard title={t('Recommendations.targetingTitle')}>
      <div className="audience-wrapper">
        {platforms.includes(TARGETING_PLATFORMS.fb) && (
          <MetaAudienceTargetingField
            tcTargeting={tcTargeting}
            onChange={onAudienceChangeInternal}
            disabled={audienceDisabled}
            event={event}
            bothSelected={platforms.includes(TARGETING_PLATFORMS.google)}
          />
        )}
        {platforms.includes(TARGETING_PLATFORMS.google) && (
          <GoogleAudienceTargetingField
            tcTargeting={tcTargeting}
            onChange={onAudienceChangeInternal}
            disabled={audienceDisabled}
            bothSelected={platforms.includes(TARGETING_PLATFORMS.fb)}
          />
        )}
      </div>

      <div className="targeting creative-composer-section-wrapper w-100 gap-2">
        <GeoTargetingField
          tc={tc}
          tc_run_id={tc_run_id}
          disabled={targetingDisabled}
          modeLocked={isPublished}
          onChange={onChangeInternal}
          showValidationErrors={showValidationErrors}
          savedAudienceSelected={savedAudienceSelected}
          hasTargetingLimitations={hasTargetingLimitations}
          isFirstTc={isFirstTc}
          event={event}
        />
        {showSuccess && <img src={greenCheck} alt="valid" className="targeting-success" />}
        {loading && (
          <LoadingSpinner className="targeting-loading" size={SpinnerSize.SMALL} showText={false} />
        )}
      </div>

      {isGoogleDisplay && (
        <p className="targeting-campaign-wide-location-summary mt-3">
          {isFirstTc
            ? t('Recommendations.geoTargeting.rangeLocations.campaignWideLocationFirstTc')
            : t('Recommendations.geoTargeting.rangeLocations.campaignWideLocationOtherTc')}
        </p>
      )}
      {error && <p style={{ color: '#FA2B37' }}>{t(error)}</p>}
    </AccordionCard>
  )
}

Targeting.propTypes = {
  tc: PropTypes.string.isRequired,
  tc_run_id: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  isPublished: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  showValidationErrors: PropTypes.bool,
  savedAudienceSelected: PropTypes.bool,
  isFirstTc: PropTypes.bool,
}
